import { Link } from "react-router-dom";

function ServiceBlurb({ icon, title, description }: { icon: string; title: string; description?: string }) {
  return (
    <div className="flex items-start gap-4 mb-6">
      <div className="w-12 h-12 flex items-center justify-center text-emerald-600 text-xl shrink-0">
        <i className={icon}></i>
      </div>
      <div>
        <h4 className="text-base font-bold text-gray-900 mb-1">{title}</h4>
        {description && <p className="text-sm text-gray-600 leading-relaxed">{description}</p>}
      </div>
    </div>
  );
}

export default function PoolMaintenance() {
  return (
    <div>
      {/* Hero */}
      <section className="relative bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900 py-20 md:py-28">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <h1 className="text-3xl md:text-5xl font-bold text-white">Pool Maintenance Services</h1>
        </div>
      </section>

      {/* Intro + Services */}
      <section className="py-12 md:py-16 bg-white">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8">
          <div className="text-center mb-8">
            <p className="text-sm md:text-base text-gray-700 leading-relaxed max-w-4xl mx-auto">
              <strong>
                Make pool ownership worry-free with our comprehensive pool maintenance services in{" "}
                <a
                  href="https://www.google.com/maps/place/Pearl+River+County,+MS"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-emerald-700 underline"
                >
                  the Gulf Coast region
                </a>
                . From seasonal openings and closings to regular cleanings and equipment upkeep, we handle it all so you can focus on enjoying your pool.
              </strong>
            </p>
          </div>

          <div className="w-full h-px bg-gray-200 mb-10"></div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16">
            {/* Left Column */}
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-gray-900 mb-6">
                <strong>Our Pool Care Services Include:</strong>
              </h2>

              <ServiceBlurb
                icon="ri-sailboat-line"
                title="Pool Openings & Closings"
                description="Start and end your swimming season seamlessly with professional pool openings and closings."
              />
              <ServiceBlurb
                icon="ri-bubble-chart-line"
                title="Weekly Pool Cleanings"
                description="Allow us to assist you in maintaining a sparkling clean pool through regular vacuuming, skimming, and debris removal."
              />
              <ServiceBlurb
                icon="ri-water-flash-line"
                title="Pool Water Testing & Balancing"
                description="Ensure your pool water is safe and balanced with our expert testing and chemical adjustments."
              />
              <ServiceBlurb
                icon="ri-tools-line"
                title="Plumbing Repairs & Leak Detections"
                description="Extend the life of your pool equipment with thorough inspections, repairs, and replacements."
              />
              <ServiceBlurb
                icon="ri-test-tube-line"
                title="Pool Chemical Supplies"
                description="We offer a complete range of chemicals to keep your pool sparkling clean all summer long. Our experts are also available to provide valuable advice on maintaining balanced and pristine pool water."
              />
              <ServiceBlurb
                icon="ri-fire-line"
                title="Pool Heating Systems"
                description="Enhance your swimming experience by adding or upgrading your pool heating pump, allowing you to extend your swimming season even through Mississippi's warm climate."
              />
            </div>

            {/* Right Column */}
            <div>
              <div className="mb-8">
                <img
                                  src="/images/iStock-1398757897-1.jpg"
                  alt="Pool maintenance"
                  className="w-full h-auto rounded-lg border border-gray-200"
                  width={1154}
                  height={769}
                />
              </div>

              <ServiceBlurb
                icon="ri-stack-line"
                title="Pool Liner Replacements"
                description="The lifespan of your pool liner can be influenced by several factors, including the liner's quality, exposure to sunlight, chemicals, weather conditions, and more. We offer a selection of high-quality pool liners for both above-ground and in-ground pools."
              />
              <ServiceBlurb
                icon="ri-filter-line"
                title="Pool Parts & Filter replacements"
                description="We offer a wide selection of pool parts and filter replacements to ensure you can get your pool up and running efficiently and promptly."
              />
              <ServiceBlurb
                icon="ri-shield-check-line"
                title="Safety Covers"
                description="Safeguard your pool from debris while ensuring the safety of your family and pets."
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 md:py-20 bg-gradient-to-r from-slate-900 via-slate-800 to-slate-900">
        <div className="max-w-[1400px] mx-auto px-4 md:px-8 text-center">
          <Link
            to="/contact"
            className="inline-block bg-emerald-600 hover:bg-emerald-700 text-white font-semibold px-8 py-4 rounded-md text-sm md:text-base transition-colors whitespace-nowrap"
          >
            Contact Us To Schedule A Service
          </Link>
        </div>
      </section>
    </div>
  );
}